import { useRef } from "react";
import { ClassroomDetails } from "@/stores/classroomStore_I";
import * as api from "@/js/api/classApi";
import { hydrateClassroomPersonalisation } from "@/components/SeatPlan/hydrateClassroomLayout";
import { useSeatPlanStore } from "@/stores/v2/seatPlanStore";
import { UseQueryResult, useQuery } from "@tanstack/react-query";

const keyPrefix = "classroomDetails";

export function useClassroomDetailsQuery(
	classroomId: string,
	includeStudents: boolean,
	includeTeachers: boolean,
	includeRules: boolean,
	type: "CLASSROOM" | "GROUP",
	sourceId: string,
	teacherId: string,
	queryOptions = {}
): UseQueryResult<{ classroom: ClassroomDetails }> {
	const lastHydratedClassroomIdRef = useRef<string | null>(null);

	const key = [keyPrefix, classroomId, includeStudents, includeTeachers, includeRules, type, sourceId, teacherId];
	const query = useQuery(
		key,
		async () => {
			const data = await api.getClassroomDetails(classroomId, includeStudents, includeTeachers, includeRules, type, sourceId, teacherId);
			// Hydrate even when the class has no saved layout: this resets the
			// (global) seat plan store to the roster-derived default, so skipping it here would leave
			// the previously opened class's grid in place.
			if (lastHydratedClassroomIdRef.current !== classroomId) {
				hydrateClassroomPersonalisation(data.classroom.personalisation, data.classroom.students ?? []);
				// Lets seat plan views tell this class's layout from the one left behind by the class
				// the teacher came from, which is what they render from until this resolves.
				useSeatPlanStore.getState().setHydratedClassroom(data.classroom.stableIdV1);
				lastHydratedClassroomIdRef.current = classroomId;
			}
			return data;
		},
		queryOptions
	);
	return query;
}

export function makeClassroomDetailsInvalidationKey(classroomId: string): string[] {
	return [keyPrefix, classroomId];
}
